import {
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Link,
  Select,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { handleHookFormHTTPError } from "src/common/form";
import { useKy } from "src/common/ky";
import { CellKitCarrier } from "src/common/types";
import { PageContent } from "src/layout/PageContent";
import {
  PageHeader,
  PageHeaderBackLink,
  PageHeaderSubtitle,
  PageHeaderTitle,
} from "src/layout/PageHeader";
import { CELL_KITS_URL } from "src/routes/ProductUnits/CellKits/index";

interface CellKitValues {
  serialNumber: string;
  simNumber: string;
  modemId: string;
  carrier?: CellKitCarrier;
  notes: string;
}

const CellKitValidationSchema = Yup.object().shape({
  serialNumber: Yup.string().required().label("Serial Number"),
  simNumber: Yup.string().required().label("SIM Number"),
  modemId: Yup.string().required().label("Modem ID"),
  carrier: Yup.string()
    .optional()
    .oneOf(Object.values(CellKitCarrier))
    .label("Carrier"),
  notes: Yup.string().label("Notes"),
});

export const CellKitAdd = () => {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setError,
    getValues,
  } = useForm<CellKitValues>({
    defaultValues: {
      serialNumber: "",
      simNumber: "",
      modemId: "",
      carrier: undefined,
      notes: "",
    },
    resolver: yupResolver(CellKitValidationSchema),
  });
  const ky = useKy();

  const navigate = useNavigate();
  const toast = useToast();
  const addCellKit = useMutation(
    (values: CellKitValues) => ky.post("cell-kits", { json: values }),
    {
      onError: handleHookFormHTTPError(setError, getValues, toast),
      onSuccess: async () => {
        toast({
          description: "Cell Kit successfully added",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
        navigate(CELL_KITS_URL);
      },
    }
  );

  return (
    <>
      <PageHeader>
        <PageHeaderBackLink>
          <Link as={RouterLink} to={"/product-units/cell-kits"}>
            &#8249; Back to list
          </Link>
        </PageHeaderBackLink>
        <PageHeaderTitle>Add Cell Kit to Inventory</PageHeaderTitle>
        <PageHeaderSubtitle>Enter Cell Kit details here</PageHeaderSubtitle>
      </PageHeader>
      <PageContent>
        <form
          onSubmit={handleSubmit((data: CellKitValues) =>
            addCellKit.mutate(data)
          )}
        >
          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <GridItem colSpan={1}>
              <VStack spacing={4}>
                <Controller
                  name="serialNumber"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl
                      isRequired
                      isInvalid={fieldState.invalid && fieldState.isTouched}
                    >
                      <FormLabel htmlFor="serialNumber">
                        Serial Number
                      </FormLabel>
                      <Input
                        {...field}
                        onChange={(event) =>
                          field.onChange(event.target.value.toUpperCase())
                        }
                        id="serialNumber"
                      />
                      <FormErrorMessage>
                        {fieldState.error?.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                />
                <Controller
                  name="simNumber"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl
                      isRequired
                      isInvalid={fieldState.invalid && fieldState.isTouched}
                    >
                      <FormLabel htmlFor="simNumber">SIM Number</FormLabel>
                      <Input {...field} id="simNumber" />
                      <FormErrorMessage>
                        {fieldState.error?.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                />
                <Controller
                  name="modemId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl
                      isRequired
                      isInvalid={fieldState.invalid && fieldState.isTouched}
                    >
                      <FormLabel htmlFor="modemId">Modem ID</FormLabel>
                      <Input {...field} id="modemId" />
                      <FormErrorMessage>
                        {fieldState.error?.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                />
                <Controller
                  name="carrier"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl
                      isInvalid={fieldState.invalid && fieldState.isTouched}
                    >
                      <FormLabel htmlFor="carrier">Carrier</FormLabel>
                      <Select {...field} id="carrier">
                        <option value="" />
                        {Object.values(CellKitCarrier).map((carrier) => (
                          <option value={carrier} key={carrier}>
                            {carrier}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage>
                        {fieldState.error?.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                />
                <Controller
                  name="notes"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl
                      isInvalid={fieldState.invalid && fieldState.isTouched}
                    >
                      <FormLabel htmlFor="notes">Notes</FormLabel>
                      <Textarea {...field} id="notes" />
                      <FormErrorMessage>
                        {fieldState.error?.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                />
              </VStack>
            </GridItem>

            <GridItem colSpan={2}>
              <Divider />
            </GridItem>

            <GridItem colSpan={2}>
              <ButtonGroup display="flex" justifyContent="flex-end">
                <Link as={RouterLink} to={CELL_KITS_URL}>
                  <Button>Cancel</Button>
                </Link>
                <Button
                  type="submit"
                  colorScheme="brand.blue"
                  isLoading={isSubmitting}
                >
                  Add Cell Kit
                </Button>
              </ButtonGroup>
            </GridItem>
          </Grid>
        </form>
      </PageContent>
    </>
  );
};
